import React, { useEffect } from "react";
import { createPortal } from "react-dom";

/**
 * Lightweight, dependency-free modal/dialog used across the app in place of
 * MUI's Dialog. Backdrop click + Escape both close it.
 *
 * Rendered through a portal to <body> so the fixed overlay always covers the
 * full viewport and stays centered — even when an ancestor has a CSS transform
 * (e.g. an entrance animation), which would otherwise trap `position: fixed`.
 */
export default function Modal({ open, onClose, children, panelClassName = "" }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    // Lock background scroll while the modal is open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/75 backdrop-blur-md animate-fadeIn"
      onClick={onClose}
    >
      <div
        className={`relative max-h-[92vh] overflow-y-auto animate-fadeUp ${panelClassName}`}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
